import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Save, ArrowRight, AlertCircle, Check } from "lucide-react";
import { ScopeTagInput } from "./ScopeTagInput";
import { GenerateButton } from "./GenerateButton";
import { TabHelperBox } from "./TabHelperBox";
import { useThematicMaps } from "@/hooks/useThematicMaps";
import { useWebsites } from "@/hooks/useWebsites";
import { useMapGenerator } from "@/hooks/useMapGenerator";
import type { ThematicMap } from "@/types/thematicMaps";

interface MacroTabProps {
  map: ThematicMap;
  onSaved?: (updates: Partial<ThematicMap>) => void;
  onNext?: () => void;
}

export function MacroTab({ map, onSaved, onNext }: MacroTabProps) {
  const { updateMap } = useThematicMaps();
  const { websites } = useWebsites();
  const { generating, runMacroGeneration } = useMapGenerator(map.id);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState(map.name);
  const [websiteId, setWebsiteId] = useState(map.website_id || "");
  const [macroStatement, setMacroStatement] = useState(map.macro_statement);
  const [scopeIn, setScopeIn] = useState<string[]>(map.scope_in || []);
  const [scopeOut, setScopeOut] = useState<string[]>(map.scope_out || []);
  const [audience, setAudience] = useState(map.audience);
  const [language, setLanguage] = useState(map.language);

  // Sync when map data changes (e.g. realtime, re-fetch, tab switch)
  useEffect(() => {
    setName(map.name);
    setWebsiteId(map.website_id || "");
    setMacroStatement(map.macro_statement);
    setScopeIn(map.scope_in || []);
    setScopeOut(map.scope_out || []);
    setAudience(map.audience);
    setLanguage(map.language);
  }, [
    map.id,
    map.macro_statement,
    map.scope_in,
    map.scope_out,
    map.audience,
    map.language,
    map.name,
    map.website_id,
  ]);

  const handleSave = async () => {
    setSaving(true);
    const updates = {
      name,
      website_id: websiteId || null,
      macro_statement: macroStatement,
      scope_in: scopeIn,
      scope_out: scopeOut,
      audience,
      language,
    };
    const success = await updateMap(map.id, updates);
    if (success) {
      onSaved?.(updates);
    }
    setSaving(false);
  };

  // Dirty detection — porownuje aktualny stan z propsem map
  const isDirty = useMemo(() => {
    return (
      name !== map.name ||
      (websiteId || null) !== (map.website_id || null) ||
      macroStatement !== map.macro_statement ||
      JSON.stringify(scopeIn) !== JSON.stringify(map.scope_in || []) ||
      JSON.stringify(scopeOut) !== JSON.stringify(map.scope_out || []) ||
      audience !== map.audience ||
      language !== map.language
    );
  }, [name, websiteId, macroStatement, scopeIn, scopeOut, audience, language, map]);

  // Empty state — krytyczne pola niewypelnione (3 pola: macro statement + scope_in + scope_out)
  const missingFields: string[] = [];
  if (!macroStatement.trim()) missingFields.push("Macro Statement");
  if (scopeIn.length === 0) missingFields.push("Scope IN");
  if (scopeOut.length === 0) missingFields.push("Scope OUT");
  const isEmpty = missingFields.length > 0;

  // Stany bannera: empty (czerwony) > dirty (amber) > saved (zielony)
  const bannerState = isEmpty ? "empty" : isDirty ? "dirty" : "saved";

  return (
    <div className="space-y-6 w-full">
      {/* Sticky banner — empty / dirty / saved (kompaktowy ~32px) */}
      <div className={`sticky top-[140px] z-20 flex items-center justify-between gap-2 px-3.5 py-1.5 rounded-md border transition-colors ${
        bannerState === "empty"
          ? "bg-red-500/8 border-red-500/30"
          : bannerState === "dirty"
            ? "bg-amber-500/8 border-amber-500/30"
            : "bg-emerald-500/8 border-emerald-500/25"
      }`}>
        <div className="flex items-center gap-1.5 text-xs font-medium min-w-0">
          {bannerState === "empty" ? (
            <>
              <AlertCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
              <span className="text-red-700 dark:text-red-300 truncate">
                Uzupelnij dane: <span className="font-semibold">{missingFields.join(", ")}</span>
              </span>
            </>
          ) : bannerState === "dirty" ? (
            <>
              <AlertCircle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
              <span className="text-amber-700 dark:text-amber-300 truncate">
                Niezapisane zmiany — kliknij Zapisz
              </span>
            </>
          ) : (
            <>
              <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0" strokeWidth={3} />
              <span className="text-emerald-700 dark:text-emerald-300 truncate">
                Wszystkie zmiany zapisane
              </span>
            </>
          )}
        </div>
        <Button
          onClick={handleSave}
          disabled={saving || (!isDirty && bannerState !== "empty")}
          size="sm"
          className={`shrink-0 h-7 px-2.5 text-xs ${
            bannerState === "empty"
              ? "bg-red-500 hover:bg-red-600 text-white"
              : isDirty
                ? "bg-amber-500 hover:bg-amber-600 text-white"
                : ""
          }`}
        >
          <Save className="mr-1 h-3 w-3" />
          {saving ? "Zapisywanie..." : "Zapisz"}
        </Button>
      </div>

      <TabHelperBox
        storageKey="macroStrategy"
        items={[
          '<strong>Automatycznie:</strong> Wybierz domene, kliknij "Generuj z AI" — uzupelni Macro, Scope IN/OUT.',
          "<strong>Recznie:</strong> Wpisz Macro Statement, dodaj tematy do Scope IN/OUT (Enter po kazdym lub wklej liste oddzielona przecinkami).",
          "Po uzupelnieniu kliknij <strong>Zapisz</strong>.",
        ]}
      />

      <div className="space-y-2">
        <Label htmlFor="macro-name">Nazwa mapy</Label>
        <Input
          id="macro-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="macro-website">Domena</Label>
        <Select value={websiteId} onValueChange={setWebsiteId}>
          <SelectTrigger>
            <SelectValue placeholder="Wybierz domenę..." />
          </SelectTrigger>
          <SelectContent>
            {websites.map((w) => (
              <SelectItem key={w.id} value={w.id}>
                {w.name} ({w.url})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="macro-statement">Macro Statement</Label>
          <GenerateButton
            onClick={async () => {
              const website = websites.find((w) => w.id === websiteId);
              const result = await runMacroGeneration(
                map,
                website?.name,
                website?.url,
              );
              if (result) {
                setMacroStatement(result.macro_statement);
                setScopeIn(result.scope_in || []);
                setScopeOut(result.scope_out || []);

                // Auto-save po generacji AI
                const updates = {
                  macro_statement: result.macro_statement,
                  scope_in: result.scope_in || [],
                  scope_out: result.scope_out || [],
                };
                const saved = await updateMap(map.id, updates);
                if (saved) {
                  onSaved?.(updates);
                }
              }
            }}
            generating={generating}
            label="Generuj z AI"
            loadingLabel="Generowanie macro..."
          />
        </div>
        <Textarea
          id="macro-statement"
          value={macroStatement}
          onChange={(e) => setMacroStatement(e.target.value)}
          placeholder="Główne stwierdzenie tematyczne mapy, np. 'Wole Sushi jest najlepszą restauracją sushi w Krakowie...'"
          rows={4}
        />
      </div>

      <ScopeTagInput
        label="Scope IN (tematy w zakresie)"
        tags={scopeIn}
        onChange={setScopeIn}
        placeholder="np. sushi, kuchnia japońska, Kraków..."
      />

      <ScopeTagInput
        label="Scope OUT (tematy wykluczone)"
        tags={scopeOut}
        onChange={setScopeOut}
        placeholder="np. inne restauracje, fast food..."
      />

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="macro-audience">Audience</Label>
          <Input
            id="macro-audience"
            value={audience}
            onChange={(e) => setAudience(e.target.value)}
            placeholder="np. miłośnicy kuchni japońskiej"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="macro-language">Język</Label>
          <Select value={language} onValueChange={setLanguage}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pl">Polski</SelectItem>
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="de">Deutsch</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {onNext && (
        <div className="flex items-center justify-end pt-4 border-t border-border/40">
          <Button
            onClick={onNext}
            variant="default"
            disabled={isDirty || isEmpty}
            title={isEmpty ? "Najpierw uzupelnij wymagane pola" : isDirty ? "Najpierw zapisz zmiany aby przejsc dalej" : ""}
          >
            Następny krok: SBD
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
